"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";

import type {
  IntegrationGroupId,
  IntegrationMapNode,
} from "@/lib/content/integration-showcase";
import { cn } from "@/lib/utils";

type Point = { x: number; y: number };

type GroupSummary = { id: IntegrationGroupId; label: string; caption: string };

type HubLayout = {
  width: number;
  height: number;
  hub: Point & { width: number; height: number };
  /** Half the petal width at its widest, in layout units. */
  petal: number;
  tiles: Record<string, Point & { size: number }>;
  /** Faint MCP clients fanning out of the MCP tile; strength sets their opacity. */
  clients: (Point & { size: number; strength: number })[];
  clientsCaption: Point;
  /**
   * Group captions drawn inside the stage, pinned by their top or bottom edge.
   * Phones put them above and below the stage instead.
   */
  groups?: Record<
    IntegrationGroupId,
    Point & { align: "left" | "center" | "right"; pin: "top" | "bottom" }
  >;
};

/**
 * The approved 1200×720 "two-way hub", moved 74 units down inside a taller
 * stage. Captions keep one 64-unit margin from the stage edges and about
 * 49 units (40 px) from their group, well clear of the 8 px gap between a tile
 * and its own label, so each reads as a group title.
 */
const wideLayout: HubLayout = {
  width: 1200,
  height: 874,
  hub: { x: 600, y: 454, width: 236, height: 112 },
  petal: 13,
  tiles: {
    claude: { x: 430, y: 252, size: 60 },
    chatgpt: { x: 604, y: 202, size: 64 },
    "mcp-server": { x: 772, y: 270, size: 56 },
    linear: { x: 1030, y: 360, size: 60 },
    notion: { x: 1062, y: 494, size: 56 },
    clickup: { x: 958, y: 604, size: 60 },
    asana: { x: 800, y: 674, size: 56 },
    slack: { x: 166, y: 434, size: 64 },
    telegram: { x: 292, y: 622, size: 58 },
  },
  clients: [
    { x: 868, y: 206, size: 30, strength: 1 },
    { x: 948, y: 162, size: 24, strength: 0.74 },
    { x: 958, y: 260, size: 24, strength: 0.72 },
    { x: 1036, y: 202, size: 20, strength: 0.52 },
    { x: 1098, y: 144, size: 16, strength: 0.36 },
    { x: 1112, y: 254, size: 14, strength: 0.32 },
  ],
  clientsCaption: { x: 1136, y: 64 },
  groups: {
    ai: { x: 600, y: 64, align: "center", pin: "top" },
    chat: { x: 64, y: 810, align: "left", pin: "bottom" },
    work: { x: 1136, y: 810, align: "right", pin: "bottom" },
  },
};

/**
 * Phones and tablets: the same hub and clockwise order of tools, packed into a
 * portrait stage. Group captions sit above and below the stage.
 */
const tallLayout: HubLayout = {
  width: 360,
  height: 500,
  hub: { x: 180, y: 250, width: 150, height: 72 },
  petal: 8,
  tiles: {
    chatgpt: { x: 180, y: 70, size: 44 },
    "mcp-server": { x: 282, y: 128, size: 42 },
    linear: { x: 318, y: 236, size: 42 },
    notion: { x: 310, y: 336, size: 42 },
    clickup: { x: 258, y: 420, size: 44 },
    asana: { x: 166, y: 452, size: 42 },
    telegram: { x: 72, y: 396, size: 42 },
    slack: { x: 44, y: 262, size: 44 },
    claude: { x: 70, y: 128, size: 44 },
  },
  clients: [
    { x: 326, y: 86, size: 20, strength: 1 },
    { x: 316, y: 40, size: 14, strength: 0.72 },
    { x: 346, y: 52, size: 14, strength: 0.56 },
    { x: 350, y: 106, size: 12, strength: 0.4 },
  ],
  clientsCaption: { x: 360, y: 0 },
};

/** Out (white) and back (grey) dot timings per tool, offset so the dots never march in step. */
const dotDelays: Record<string, [number, number]> = {
  claude: [-0.3, -0.6],
  chatgpt: [-1.1, -0.2],
  "mcp-server": [-1.7, -0.8],
  linear: [-0.5, -0.4],
  notion: [-1.4, -0.9],
  clickup: [-0.8, -0.1],
  asana: [-1.9, -0.5],
  slack: [-1.2, -0.7],
  telegram: [-0.1, -0.3],
};
const clientDelays = [-0.2, -0.9, -0.5, -1.1, -0.3, -0.7];

/**
 * A petal is two cubic curves between the hub and a tile that both bend
 * clockwise: out along the outer edge, back along the inner edge.
 */
function petal(hub: Point, tile: Point, halfWidth: number) {
  const dx = tile.x - hub.x;
  const dy = tile.y - hub.y;
  const length = Math.hypot(dx, dy);
  const side = { x: -dy / length, y: dx / length };
  const at = (along: number, offset: number) =>
    `${(hub.x + dx * along + side.x * offset).toFixed(1)} ${(hub.y + dy * along + side.y * offset).toFixed(1)}`;
  const outer = length * 0.15 + halfWidth;
  const inner = length * 0.15 - halfWidth;
  const start = `${hub.x} ${hub.y}`;
  const end = `${tile.x} ${tile.y}`;

  return {
    out: `M ${start} C ${at(0.3, outer)}, ${at(0.7, outer)}, ${end}`,
    back: `M ${end} C ${at(0.7, inner)}, ${at(0.3, inner)}, ${start}`,
    shape: `M ${start} C ${at(0.3, outer)}, ${at(0.7, outer)}, ${end} C ${at(0.7, inner)}, ${at(0.3, inner)}, ${start} Z`,
  };
}

/** A gentle clockwise arc from the MCP tile to one of its clients. */
function fan(from: Point, to: Point) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const via = { x: (from.x + to.x) / 2 - dy * 0.18, y: (from.y + to.y) / 2 + dx * 0.18 };

  return `M ${from.x} ${from.y} Q ${via.x.toFixed(1)} ${via.y.toFixed(1)}, ${to.x} ${to.y}`;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);

    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);

  return matches;
}

export function IntegrationMap({
  nodes,
  groups,
  hubCaption,
}: {
  nodes: readonly IntegrationMapNode[];
  groups: readonly GroupSummary[];
  hubCaption: string;
}) {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const wide = useMediaQuery("(min-width: 1024px)");
  const [inView, setInView] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const root = rootRef.current;

    if (!root) return;

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));

    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // inView only turns true after mount, so dots never animate in server HTML.
  const animate = inView && !reducedMotion;
  const group = (id: IntegrationGroupId) => groups.find((candidate) => candidate.id === id);

  return (
    <div
      ref={rootRef}
      className="overflow-hidden rounded-[1.75rem] bg-[#09090B] bg-[radial-gradient(ellipse_62%_58%_at_50%_52%,#1D1D22_0%,#0E0E11_60%,#09090B_100%)] px-4 py-6 text-[#FAFAFA] lg:p-0"
    >
      <HubStage
        layout={wideLayout}
        nodes={nodes}
        groups={groups}
        hubCaption={hubCaption}
        animate={animate && wide}
        className="hidden lg:block"
      />

      <div className="mx-auto max-w-[28rem] lg:hidden">
        <GroupCaption group={group("ai")} align="center" />
        <HubStage
          layout={tallLayout}
          nodes={nodes}
          groups={groups}
          hubCaption={hubCaption}
          animate={animate && !wide}
          className="mt-5"
        />
        <div className="mt-4 grid grid-cols-2 gap-4">
          <GroupCaption group={group("chat")} align="left" />
          <GroupCaption group={group("work")} align="right" />
        </div>
      </div>

      <ul className="sr-only">
        {nodes.map((node) => (
          <li key={node.slug}>
            {node.label}: {node.exchange}
          </li>
        ))}
      </ul>
    </div>
  );
}

function GroupCaption({
  group,
  align,
  className,
  style,
}: {
  group?: GroupSummary;
  align: "left" | "center" | "right";
  className?: string;
  style?: React.CSSProperties;
}) {
  if (!group) return null;

  return (
    <div
      aria-hidden="true"
      className={cn(
        align === "center" && "text-center",
        align === "right" && "text-right",
        className,
      )}
      style={style}
    >
      <p className="text-base font-semibold leading-6 text-[#FAFAFA]">{group.label}</p>
      <p className="mt-0.5 text-sm leading-5 text-balance text-[#A1A1AA]">{group.caption}</p>
    </div>
  );
}

function HubStage({
  layout,
  nodes,
  groups,
  hubCaption,
  animate,
  className,
}: {
  layout: HubLayout;
  nodes: readonly IntegrationMapNode[];
  groups: readonly GroupSummary[];
  hubCaption: string;
  animate: boolean;
  className?: string;
}) {
  const { hub, groups: placements } = layout;
  const mcp = layout.tiles["mcp-server"];
  const left = (x: number) => `${(x / layout.width) * 100}%`;
  const top = (y: number) => `${(y / layout.height) * 100}%`;
  // Sizes follow the stage width, so the composition scales as one drawing.
  const unit = (value: number) => `${((value / layout.width) * 100).toFixed(3)}cqw`;

  return (
    <div
      className={cn("relative w-full select-none [container-type:inline-size]", className)}
      style={{ aspectRatio: `${layout.width} / ${layout.height}` }}
    >
      <svg
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        className="absolute inset-0 size-full overflow-visible"
        fill="none"
        strokeLinecap="round"
        aria-hidden="true"
      >
        {nodes.map((node) => {
          const tile = layout.tiles[node.slug];
          const shape = petal(hub, tile, layout.petal);
          const [outDelay, backDelay] = dotDelays[node.slug] ?? [0, 0];

          return (
            <g key={node.slug}>
              <path d={shape.shape} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.10)" />
              <path
                d={shape.out}
                stroke="#FFFFFF"
                strokeWidth="2.4"
                strokeDasharray="0.1 15 0.1 33"
                className={animate ? "cooperly-hub-out" : undefined}
                style={{ animationDelay: `${outDelay}s` }}
              />
              <path
                d={shape.back}
                stroke="#A1A1AA"
                strokeWidth="2"
                strokeDasharray="0.1 21"
                className={animate ? "cooperly-hub-back" : undefined}
                style={{ animationDelay: `${backDelay}s` }}
              />
            </g>
          );
        })}

        {layout.clients.map((client, index) => {
          const d = fan(mcp, client);

          return (
            <g key={`${client.x}-${client.y}`}>
              <path
                d={d}
                stroke={`rgba(255,255,255,${(0.3 * client.strength).toFixed(2)})`}
                strokeDasharray="2 5"
              />
              <path
                d={d}
                stroke={`rgba(255,255,255,${(0.75 * client.strength).toFixed(2)})`}
                strokeWidth="2"
                strokeDasharray="0.1 12"
                className={animate ? "cooperly-hub-far" : undefined}
                style={{ animationDelay: `${clientDelays[index % clientDelays.length]}s` }}
              />
            </g>
          );
        })}
      </svg>

      {layout.clients.map((client) => (
        <span
          key={`${client.x}-${client.y}`}
          aria-hidden="true"
          className="absolute -translate-x-1/2 -translate-y-1/2 border"
          style={{
            left: left(client.x),
            top: top(client.y),
            width: unit(client.size),
            height: unit(client.size),
            borderRadius: unit(client.size * 0.3),
            borderColor: `rgba(255,255,255,${(0.5 * client.strength).toFixed(2)})`,
            backgroundColor: `rgba(255,255,255,${(0.07 * client.strength).toFixed(3)})`,
          }}
        />
      ))}
      <span
        aria-hidden="true"
        className="absolute -translate-x-full text-xs leading-4 whitespace-nowrap text-[#8E8E96]"
        style={{ left: left(layout.clientsCaption.x), top: top(layout.clientsCaption.y) }}
      >
        Any MCP client
      </span>

      {placements
        ? groups.map((group) => {
            const place = placements[group.id];

            return (
              <GroupCaption
                key={group.id}
                group={group}
                align={place.align}
                className={cn(
                  "absolute w-[25cqw]",
                  place.align === "center" && "w-[40cqw] -translate-x-1/2",
                  place.align === "right" && "-translate-x-full",
                  place.pin === "bottom" && "-translate-y-full",
                )}
                style={{ left: left(place.x), top: top(place.y) }}
              />
            );
          })
        : null}

      {animate
        ? [0, -1.6].map((delay) => (
            <span
              key={delay}
              aria-hidden="true"
              className="cooperly-hub-ring absolute -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/50"
              style={{
                left: left(hub.x),
                top: top(hub.y),
                width: unit(hub.width),
                height: unit(hub.height),
                animationDelay: `${delay}s`,
              }}
            />
          ))
        : null}
      <div
        className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-[#FAFAF9]"
        style={{
          left: left(hub.x),
          top: top(hub.y),
          width: unit(hub.width),
          height: unit(hub.height),
          gap: unit(hub.height * 0.09),
          boxShadow: `0 0 0 ${unit(10)} rgba(255,255,255,0.05), 0 0 0 ${unit(22)} rgba(255,255,255,0.025), 0 0 ${unit(90)} ${unit(12)} rgba(255,255,255,0.14)`,
        }}
      >
        <Image
          src="/cooperly/cooperly_logo.svg"
          alt="Cooperly"
          width={785}
          height={138}
          style={{ width: unit(hub.width * 0.67), height: "auto" }}
        />
        <span className="text-xs leading-4 tracking-[0.01em] text-zinc-600 lg:text-sm lg:leading-5">
          {hubCaption}
        </span>
      </div>

      {nodes.map((node) => {
        const tile = layout.tiles[node.slug];

        return (
          <Link
            key={node.slug}
            href={node.href}
            aria-label={`${node.label} integration`}
            className="group absolute flex -translate-x-1/2 flex-col items-center gap-1.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#09090B] lg:gap-2"
            style={{ left: left(tile.x), top: top(tile.y - tile.size / 2) }}
          >
            <span
              className="site-pressable flex items-center justify-center bg-white shadow-[0_0_0_1px_rgba(255,255,255,0.14),0_16px_32px_-10px_rgba(0,0,0,0.7),0_0_32px_rgba(255,255,255,0.10)] group-hover:shadow-[0_0_0_1px_rgba(255,255,255,0.5),0_16px_32px_-10px_rgba(0,0,0,0.7),0_0_40px_rgba(255,255,255,0.18)]"
              style={{
                width: unit(tile.size),
                height: unit(tile.size),
                padding: unit(tile.size * 0.26),
                borderRadius: unit(tile.size * 0.3),
              }}
            >
              <Image
                src={node.imagePath}
                alt=""
                aria-hidden="true"
                width={32}
                height={32}
                className="size-full object-contain"
              />
            </span>
            <span className="text-xs font-medium leading-4 whitespace-nowrap text-[#E4E4E7]">
              {node.label}
            </span>
          </Link>
        );
      })}
    </div>
  );
}
